import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress
} from '@mui/material';
import { Close, Save } from '@mui/icons-material';
import { useAuth } from '../lib/supabase/AuthContext';
import { updateRdvStatus } from '../lib/supabase/leadsService';

type QualificationStatus = 'Validé' | 'Litige' | 'No show';

interface QualificationActionModalProps {
  open: boolean;
  onClose: () => void;
  rdvId: string;
  leadName: string;
  currentStatus?: string;
  onQualificationUpdated: () => void;
}

const QualificationActionModal: React.FC<QualificationActionModalProps> = ({
  open,
  onClose,
  rdvId,
  leadName,
  currentStatus,
  onQualificationUpdated
}) => {
  const { user } = useAuth();
  const [status, setStatus] = useState<QualificationStatus | ''>('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Réinitialiser les états quand le modal s'ouvre
  useEffect(() => {
    if (open) {
      setStatus('');
      setComment('');
      setError(null);
    }
  }, [open]);
  
  const isCommentRequired = status === 'Litige';
  const showCommentField = status === 'Litige' || status === 'No show';
  
  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStatus(event.target.value as QualificationStatus);
  };
  
  const handleSaveQualification = async () => {
    if (!user || !status) return;
    
    if (isCommentRequired && !comment.trim()) {
      setError('Un commentaire est requis pour "Litige"');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Déterminer si le RDV est qualifié basé sur le statut
      let etatRdv: string = 'En attente';
      
      if (status === 'Validé') {
        etatRdv = 'Validé';
      } else if (status === 'Litige') {
        etatRdv = 'Litige';
      } else if (status === 'No show') {
        etatRdv = 'No show';
      }
      
      // Commentaire pour la colonne retour_client (si applicable)
      const retourClient = status === 'Validé'
        ? undefined 
        : comment.trim() || undefined;
      
      // Commentaire pour rdv_commentaire (pour affichage dans la qualification)
      const rdvCommentaire = comment.trim() || undefined;
      
      const { error: updateError } = await updateRdvStatus(
        rdvId,
        etatRdv,
        retourClient,
        rdvCommentaire
      );
      
      if (updateError) {
        throw updateError;
      }
      
      // Notifier le composant parent que la qualification a été mise à jour
      onQualificationUpdated();
      onClose();
    } catch (err: any) {
      console.error('Erreur lors de la qualification du RDV:', err);
      setError(`Une erreur est survenue: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Qualifier le RDV - {leadName}
        </Typography>
        <IconButton onClick={onClose} size="small" disabled={loading}>
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ my: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
            <FormLabel component="legend">Statut du RDV</FormLabel>
            <RadioGroup 
              value={status} 
              onChange={handleStatusChange}
              name="qualification-status"
            >
              <FormControlLabel value="Validé" control={<Radio />} label="Validé" />
              <FormControlLabel value="Litige" control={<Radio />} label="Litige" />
              <FormControlLabel value="No show" control={<Radio />} label="No show" />
            </RadioGroup>
          </FormControl>
          
          {showCommentField && (
            <TextField
              label={status === 'Litige' ? "Commentaire (requis)" : "Commentaire (optionnel)"}
              fullWidth
              multiline
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={status === 'Litige' 
                ? "Veuillez expliquer la raison du litige" 
                : "Commentaire optionnel sur le No show"}
              required={isCommentRequired}
              error={isCommentRequired && !comment.trim()}
              helperText={isCommentRequired && !comment.trim() ? "Un commentaire est requis pour un litige" : ""}
              disabled={loading}
            />
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button 
          onClick={handleSaveQualification} 
          variant="contained" 
          color="primary"
          startIcon={loading ? <CircularProgress size={16} /> : <Save />}
          disabled={loading || !status || (isCommentRequired && !comment.trim())}
        >
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QualificationActionModal; 